import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListSubheader from "@material-ui/core/ListSubheader";
import Typography from "@material-ui/core/Typography";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { useMsal, useAccount } from "@azure/msal-react";
import { useTokenQuery } from "../../../hooks/useTokenQuery";
import { fetchWithAuth } from "../../helpers/fetchWithAuth";
import statWarsLogo from "../../../assets/images/StatWars-Logo.png";
import { trimPath } from "../../utilities";
import { useUserContextQuery } from "../../../hooks/useUserContextQuery";

const mainRoutes = [
  {
    path: "/",
    name: "Event Management",
  },
  {
    path: "/live",
    name: "Live",
  },
  {
    path: "/player-index/5DB88974-FA00-4A02-8EC2-5753A3BD0D2C",
    name: "Player Index",
  },
  {
    path: "/service-monitoring",
    name: "Service Monitoring",
  },
];

const adminRoutes = [
  {
    path: "/price-rules/5DB88974-FA00-4A02-8EC2-5753A3BD0D2C",
    name: "Default Price Rules",
  },
  {
    path: "/statwars-accounts",
    name: "StatWars Accounts",
  },
  {
    path: "/client-management",
    name: "Client Management",
  },
];

const Menu = (props) => {
  const classes = useStyles();
  const { drawerOpen } = props;
  const location = useLocation();
  const userContextQuery = useUserContextQuery();
  const isAdmin = userContextQuery.data?.isAdmin;
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const tokenQuery = useTokenQuery();
  const apiUrl = process.env.REACT_APP_API_URI.trim();

  const notifCountQuery = useQuery(
    "notification-count",
    () =>
      fetchWithAuth(
        `${apiUrl}/operator/notifications/unread-count`,
        { method: "get" },
        instance,
        account,
        inProgress,
      ),
    {
      enabled: !!accounts.length && inProgress === "none" && !!tokenQuery.data,
      refetchInterval: 30000,
      staleTime: 15000,
    },
  );
  const unreadCount = notifCountQuery.data?.count || 0;

  const [clickCount, setClickCount] = useState(0);

  const handleClick = () => {
    setClickCount(clickCount + 1);
  };
  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      open={drawerOpen}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.drawerHeader}>
        <div className={classes.logo}>
          <Link to="/">
            {clickCount > 10 ? (
              "STATE WARS UNLOCKED"
            ) : (
              <img src={statWarsLogo} alt="logo" />
            )}
          </Link>
          <Typography
            variant="caption"
            style={{ color: "#ddd" }}
            onClick={() => handleClick()}
          >
            {process.env.REACT_APP_GIT_SHA ? (
              <>
                {process.env.REACT_APP_BUILD_DATE &&
                  `${process.env.REACT_APP_BUILD_DATE} `}
                <a
                  href={`https://github.com/stride-statwars/statwars/commit/${process.env.REACT_APP_GIT_SHA}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {process.env.REACT_APP_GIT_SHA.slice(0, 7)}
                </a>
              </>
            ) : (
              "2.0.0"
            )}
          </Typography>
        </div>
      </div>
      <List className={classes.linkList}>
        {mainRoutes.map((route) => (
          <ListItem
            key={route.path}
            component={Link}
            to={route.path}
            button
            className={classes.listItem}
            selected={
              route.path === "/"
                ? location.pathname.includes("event-management")
                : trimPath(location.pathname) === trimPath(route.path)
            }
          >
            <ListItemText primary={route.name} />
          </ListItem>
        ))}
        <ListItem
          component={Link}
          to="/notifications"
          button
          className={classes.listItem}
          selected={location.pathname === "/notifications"}
        >
          <ListItemText
            primary={
              <Badge
                badgeContent={unreadCount}
                color="error"
                max={99}
                invisible={unreadCount === 0}
              >
                <span style={{ paddingRight: unreadCount > 0 ? 12 : 0 }}>
                  Notifications
                </span>
              </Badge>
            }
          />
        </ListItem>
        {isAdmin && (
          <>
            <Divider style={{ margin: "8px 0" }} />
            <ListSubheader className={classes.subheader}>Admin</ListSubheader>
            {adminRoutes.map((route) => (
              <ListItem
                key={route.path}
                component={Link}
                to={route.path}
                button
                className={classes.listItem}
                selected={location.pathname.startsWith(route.path)}
              >
                <ListItemText primary={route.name} />
              </ListItem>
            ))}
          </>
        )}
        {clickCount > 10 && (
          <ListItem
            key={"state-wars"}
            component={Link}
            to={"/state-wars"}
            button
            className={classes.listItem}
            selected={trimPath(location.pathname) === trimPath("/state-wars")}
          >
            <ListItemText primary={"States Wars"} />
          </ListItem>
        )}
      </List>
    </Drawer>
  );
};

export default Menu;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: theme.layout.drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: theme.layout.drawerWidth,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  drawerHeader: {
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  },
  logo: {
    width: "140px",
    marginRight: "auto",
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
    position: "relative",
    "& img": {
      width: "100%",
    },
    "& >  span": {
      position: "absolute",
      left: "100%",
      top: "-8px",
      marginLeft: "8px",
    },
  },
  linkList: {
    "& .MuiTypography-body1": {
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& .MuiListItem-gutters": {
      paddingLeft: theme.spacing(3),
    },
    "& .Mui-selected": {
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: `${theme.palette.common.black}15`,
    },
  },
  subheader: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    lineHeight: "32px",
    color: theme.palette.text.secondary,
  },
}));
